import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, HostListener, inject } from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import type { Observable } from 'rxjs';

import { FormBuilder, type FormControl } from '@angular/forms';
import { of } from 'rxjs';
import { take, map, filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import {
    LibraryBlockCategory,
    libraryBlockCategoryOrder,
    libraryBlockCategoryTranslations,
    type LibraryBlockEntity,
} from '@web/store/library-blocks/library-blocks.models';
import type { DenormalizedBlockEntity, WebWidgetModel } from '@web/types';
import type { BlockRole } from '@sites/entity-core';
import { blockIsShared, blockRole, toUnsharedBlockMeta } from '@sites/entity-core';

import { EDrawerType } from '@common/types/layout.type';
import { EEnvTypes } from '@common/enums/env-types.enum';

import { environment } from '@web-env/environment';

import { SpReverseArrayPipe } from '@common/pipes/reverse-array.pipe';

import { WebStructureAdapterService } from '@web/core/services/web-structure-adapter.service';
import { WebWidgetsService } from '@web/core/services/web-widgets.service';

import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { ContentFacade } from '@web/store/website/content/content.facade';
import { LibraryBlockFacade } from '@web/store/library-blocks/library-blocks.facade';
import { ModalsFacade } from '@web/store/modals/modals.facade';
import { PagesFacade } from '@web/store/website/pages/pages.facade';
import { UserFacade } from '@web/store/user/user.facade';
import { NotifyFacade } from '@common/utils/notify/notify.facade';

import { CreateBlockModalComponent } from '@web/shared/modals/create-block-modal/create-block-modal.component';
import { ToolbarWidgetComponent } from '@web/shared/components/web-toolbar-widget/web-toolbar-widget.component';
import { WebWidgetGroupPipe } from '@web/shared/pipes/widget-group.pipe';

import { BaseDrawerComponent } from '../base-drawer.component';

@Component({
    selector: 'web-block-drawer',
    templateUrl: './block-drawer.component.html',
    styleUrls: ['./block-drawer.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AsyncPipe,
        CommonModule,
        TooltipModule,
        ToolbarWidgetComponent,
        TranslateModule,
        SpReverseArrayPipe,
        WebWidgetGroupPipe,
    ],
})
export class BlockDrawerComponent extends BaseDrawerComponent implements OnInit {
    @HostListener('window:keydown.meta.k')
    public handleOpenDrawer(): void {
        const index = 0;
        this.openDrawer(index);
    }
    @HostListener('window:keydown.meta.control.k')
    public handleCreateEmpty(): void {
        this.createBlock(0, false);
    }

    public readonly isEcommerceMode$ = this.appFacade.isEcommerceMode$;

    private readonly fb: FormBuilder = inject(FormBuilder);
    private readonly translateService: TranslateService = inject(TranslateService);
    private readonly bsModalService: BsModalService = inject(BsModalService);
    private readonly widgetsService: WebWidgetsService = inject(WebWidgetsService);
    private readonly structureAdapterService: WebStructureAdapterService = inject(WebStructureAdapterService);
    private readonly blockFacade: BlocksFacade = inject(BlocksFacade);
    private readonly libraryBlocksFacade: LibraryBlockFacade = inject(LibraryBlockFacade);
    private readonly modalsFacade: ModalsFacade = inject(ModalsFacade);
    private readonly pagesFacade: PagesFacade = inject(PagesFacade);
    private readonly contentFacade: ContentFacade = inject(ContentFacade);
    private readonly userFacade: UserFacade = inject(UserFacade);
    private readonly notifyFacade: NotifyFacade = inject(NotifyFacade);

    public readonly isAdmin$: Observable<boolean> = this.appFacade.isAdmin$;
    public readonly showDrawer$: Observable<boolean> = this.layoutFacade.showBlockDrawer$;
    public readonly blockIndex$: Observable<number> = this.layoutFacade.blockIndex$;
    public readonly libraryBlocks$ = this.libraryBlocksFacade.groupedLibraryBlocks$;
    public readonly eduUserTariff$ = this.userFacade.eduUserTariff$;
    public readonly isOffice$ = this.userFacade.isOffice$;
    public readonly isEduSite$: Observable<boolean> = this.pagesFacade.isEduSite$;

    public blockIndex: number;
    public activeCategory: FormControl<LibraryBlockCategory> = this.fb.control(LibraryBlockCategory.header);
    public categoryNames = libraryBlockCategoryTranslations(this.translateService);
    public categoriesOrder = libraryBlockCategoryOrder;
    public widgetsCategoryKey = LibraryBlockCategory.widgets;
    public savedCategoryKey = LibraryBlockCategory.saved;

    public showLibraryBlocksOnMobile = false;

    public isLoadingDrawer: boolean = false;
    public currentCategoryIndex: number = 1;

    public ngOnInit(): void {
        this.blockIndex$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((index) => {
            this.blockIndex = index;
        });

        this.isEcommerceMode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isEcommerceMode) => {
            if (!isEcommerceMode) {
                return;
            }

            this.currentCategoryIndex = LibraryBlockCategory.widgets;
            this.activeCategory.setValue(LibraryBlockCategory.widgets);
        });

        this.libraryBlocks$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((blocks) => {
            this.isLoadingDrawer = true;

            if (!Object.keys(blocks).length) return;
            let hasBlocks = false;

            for (const category of this.categoriesOrder) {
                if (category in blocks) {
                    hasBlocks = true;
                    this.activeCategory.setValue(this.currentCategoryIndex);
                    break;
                }
            }

            this.isLoadingDrawer = false;

            if (!hasBlocks) {
                this.activeCategory.setValue(LibraryBlockCategory.widgets);
            }
        });
    }

    public setActiveState(categoryKey: LibraryBlockCategory): void {
        this.currentCategoryIndex = categoryKey;
        this.activeCategory.setValue(categoryKey);
    }

    public setActiveStateOnMobile(categoryKey: LibraryBlockCategory): void {
        this.currentCategoryIndex = categoryKey;
        this.activeCategory.setValue(categoryKey);
        this.toggleLibraryBlocksMobile();
    }

    public toggleLibraryBlocksMobile(): void {
        this.showLibraryBlocksOnMobile = !this.showLibraryBlocksOnMobile;
    }

    public getActiveClass(categoryKey: LibraryBlockCategory): string {
        return this.activeCategory.value == categoryKey ? 'active' : '';
    }

    public openDrawer(blockIndex: number): void {
        this.layoutFacade.openDrawer({ drawerType: EDrawerType.block, blockIndex });
    }

    public closeDrawer(): void {
        this.layoutFacade.closeDrawer();
    }

    public createBlock(index: number, isAdmin: boolean, widget?: WebWidgetModel): void {
        if (widget) {
            if (!isAdmin && (!widget?.permission?.isReleased || widget?.params?.disabled)) {
                return;
            }

            this.blockFacade.createBlock(index, widget.name);
        } else {
            this.blockFacade.createBlock(index);
        }

        this.closeDrawer();
    }

    public createSpecificBlock(index: number): void {
        this.blockFacade.createSpecificBlock(index);

        this.closeDrawer();
    }

    public createZeroBlock(index: number): void {
        this.blockFacade.createZeroBlock(index);

        this.closeDrawer();
    }

    public createFromLibraryBlock(block: DenormalizedBlockEntity[]): void {
        const blockIndex = this.blockIndex;
        this.closeDrawer();

        // The canvas only takes a block that migrates whole, and a library block has no page to be
        // carried through untouched on — so it is refused rather than pasted half-read.
        if (
            this.structureAdapterService.adaptWebStructureBeforeSet([structuredClone(block[0])]).failedBlockIndexes.size
        ) {
            this.notifyFacade.notifyAction(this.translateService.instant('library_block_not_loaded'), 'warning');
            return;
        }

        this.contentFacade.sharedBlocks$.pipe(take(1)).subscribe((sharedBlocks) => {
            const isCategoryHeaderOrFooter = [LibraryBlockCategory.header, LibraryBlockCategory.footer].includes(
                this.activeCategory.value
            );

            if (!isCategoryHeaderOrFooter) {
                // A block saved to the gallery from the site itself lands in the "Saved" category.
                // If it was a cross-page (shared) header/footer, restore it as shared — a header at
                // the top, a footer at the bottom. The site allows only a single shared header and a
                // single shared footer, so fall back to a plain block when that slot is already taken.
                const savedSharedType = this.getSavedSharedBlockType(block[0]);

                if (savedSharedType && !sharedBlocks?.[savedSharedType]) {
                    const adaptedSharedBlock = this.adaptBlock(block);
                    adaptedSharedBlock.meta = {
                        ...toUnsharedBlockMeta(adaptedSharedBlock.meta),
                        role: savedSharedType,
                        isShared: true,
                    };

                    if (savedSharedType === 'header') {
                        this.blockFacade.copyBlock(adaptedSharedBlock, 0);
                        return;
                    }

                    this.blockFacade.lastBlockIndex$.pipe(take(1)).subscribe((lastBlockIndex) => {
                        this.blockFacade.copyBlock(adaptedSharedBlock, lastBlockIndex + 1);
                    });
                    return;
                }

                if (savedSharedType === 'footer') {
                    // Footer slot already taken → paste as a plain block, dropping the cross-page flag.
                    // (A saved header is a 'specific' block, which createLibraryBlock already downgrades.)
                    const adaptedBlock = this.adaptBlock(block);
                    adaptedBlock.meta = { ...toUnsharedBlockMeta(adaptedBlock.meta), role: 'footer' };
                    this.blockFacade.copyBlock(adaptedBlock, blockIndex);
                    return;
                }

                this.createLibraryBlock(block, blockIndex);
                return;
            }

            const shareBlocksMap = {
                [LibraryBlockCategory.header]: 'header',
                [LibraryBlockCategory.footer]: 'footer',
            };

            if (sharedBlocks?.[shareBlocksMap[this.activeCategory.value]]) {
                this.createLibraryBlock(block, blockIndex);
                return;
            }

            const type = shareBlocksMap[this.activeCategory.value] as BlockRole;
            const modal = this.bsModalService.show(CreateBlockModalComponent, {
                initialState: {
                    type,
                },
            });
            modal.content.onClose.subscribe((result) => {
                if (!result) {
                    const adaptedBlock = this.adaptBlock(block);
                    adaptedBlock.meta = { ...toUnsharedBlockMeta(adaptedBlock.meta), role: type };
                    this.blockFacade.copyBlock(adaptedBlock, blockIndex);
                    return;
                }

                const adaptedBlock = this.adaptBlock(block);
                adaptedBlock.meta = { ...toUnsharedBlockMeta(adaptedBlock.meta), role: type, isShared: true };

                if (this.activeCategory.value === LibraryBlockCategory.header) {
                    this.blockFacade.copyBlock(adaptedBlock, 0);
                    return;
                }

                this.blockFacade.lastBlockIndex$.pipe(take(1)).subscribe((lastBlockIndex) => {
                    this.blockFacade.copyBlock(adaptedBlock, lastBlockIndex + 1);
                });
            });
        });
    }

    private getSavedSharedBlockType(block: DenormalizedBlockEntity): BlockRole | null {
        if (!blockIsShared(block)) {
            return null;
        }

        if (block.entitySubType === 'specific') {
            return 'header';
        }

        return blockRole(block) ?? null;
    }

    private createLibraryBlock(block: DenormalizedBlockEntity[], blockIndex: number): void {
        const adaptedBlock = this.adaptBlock(block);

        if (adaptedBlock.entitySubType === 'specific') {
            adaptedBlock.meta = toUnsharedBlockMeta(adaptedBlock.meta);
            adaptedBlock.props.pin = null;
        }

        this.blockFacade.copyBlock(adaptedBlock, blockIndex);
    }

    /**
     * A gallery block is a structure that was serialised at some point in the past, so it
     * goes through the same adapter the page structure does: it is migrated to the current
     * entity version and its parent links are re-pointed at the entities it actually sits
     * under. The ids themselves are re-issued afterwards, when `copyBlock` clones it onto
     * the page — the library ones would otherwise collide with what is already there.
     */
    private adaptBlock(block: DenormalizedBlockEntity[]): DenormalizedBlockEntity {
        return this.structureAdapterService.adaptWebStructureBeforeSet([structuredClone(block[0])]).structure[0];
    }

    public deleteBlockFromLibrary(block: LibraryBlockEntity): void {
        this.modalsFacade.showConfirmDeleteUserLibraryBlockModal(block.blockId);
    }

    public isSharedBlockCategory(category: LibraryBlockCategory): boolean {
        return category === LibraryBlockCategory.header || category === LibraryBlockCategory.footer;
    }

    public get widgets$(): Observable<WebWidgetModel[]> {
        // if (this.isSpecific) {
        //     return this.widgetsService.getSpecificWidgets$();
        // }

        return this.widgetsService.getVisibleWidgets$();
    }

    public readonly ZERO_BLOCK_BUTTON_WHITELIST_SET: Set<number> = new Set([
        6832759, 8563523, 7824090, 6778744, 9005165, 8252511, 7612472, 8226980, 6582753, 7071190, 6613114, 6802616,
    ]);
    public get showZeroBlockButton$(): Observable<boolean> {
        return of(true);

        return this.userFacade.user$.pipe(
            filter((user) => !!user),
            take(1),
            map((user) => {
                if (user.is_office) {
                    return true;
                }

                if (environment.type === EEnvTypes.PROD) {
                    return this.ZERO_BLOCK_BUTTON_WHITELIST_SET.has(user.user_id);
                }

                return true;
            })
        );
    }
}
